import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toast from 'react-hot-toast';
import { createApiUrlWithPrefix } from '../config/api';
import { 
  Calendar, 
  Users, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Clock,
  Search,
  Eye,
  User,
  Wallet,
  ChevronLeft,
  ChevronRight,
  Grid,
  List,
  Calendar as CalendarIcon,
  UserPlus,
  Edit,
  Trash2
} from 'lucide-react';
import CalendarView from './CalendarView';

import { API_BASE_URL } from '../config/api';
const EnhancedAttendanceManagement = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedSession, setSelectedSession] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [showParticipantsModal, setShowParticipantsModal] = useState(false);
  const [walletBalances, setWalletBalances] = useState({});
  const [forfaitStatuses, setForfaitStatuses] = useState({});
  const [subscriptionStatuses, setSubscriptionStatuses] = useState({});
  const [jetonStatuses, setJetonStatuses] = useState({});
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState('daily'); // 'daily', 'calendar'
  const [currentDate, setCurrentDate] = useState(new Date());
  const [sessionHorses, setSessionHorses] = useState([]);
  const [horseAttendances, setHorseAttendances] = useState({});
  const [showHorseAttendanceModal, setShowHorseAttendanceModal] = useState(false);

    const [refreshKey, setRefreshKey] = useState(0);
  
  // Manual refresh function
  const handleRefresh = async () => {
    console.log('Manual refresh triggered');
    setRefreshKey(prev => prev + 1);
    await fetchSessions();
    if (selectedSession) {
      await fetchSessionParticipants(selectedSession.id);
    }
    toast.success('Data refreshed successfully');
  };

// Fetch sessions from regular sessions API
  const fetchSessions = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/sessions`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      setSessions(response.data?.sessions || []);
    } catch (error) {
      console.error('Error fetching sessions:', error);
      toast.error(t('sessions.simplified.messages.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    fetchSessions();
  }, [fetchSessions]);

  // Clear all attendance data when date changes
  useEffect(() => {
    console.log('Date changed to:', selectedDate);
    setParticipants([]);
    setForfaitStatuses({});
    setJetonStatuses({});
    setHorseAttendances({});
    
    // If modals are open, refresh the data for the new date
    if (showParticipantsModal && selectedSession) {
      fetchSessionParticipants(selectedSession.id);
    }
    if (showHorseAttendanceModal && selectedSession) {
      fetchSessionHorses(selectedSession.id);
    }
  }, [selectedDate]);

  // Refresh participants data when selected date changes
  useEffect(() => {
    if (showParticipantsModal && selectedSession) {
      fetchSessionParticipants(selectedSession.id);
    }
  }, [selectedDate, showParticipantsModal, selectedSession]);

  // Refresh horse attendance data when selected date changes
  useEffect(() => {
    if (showHorseAttendanceModal && selectedSession) {
      fetchSessionHorses(selectedSession.id);
    }
  }, [selectedDate, showHorseAttendanceModal, selectedSession]);

  const fetchSessionParticipants = async (sessionId) => {
    try {
      const response = await axios.get(createApiUrlWithPrefix(`sessions/${sessionId}/participants`), {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        params: {
          attendanceDate: selectedDate // Pass the selected date (already in YYYY-MM-DD format)
        }
      });
      
      console.log('API Response for participants:', response.data);
      
      // Sort participants so coaches appear first
      const sortedParticipants = (response.data || []).sort((a, b) => {
        const aRole = a.user.role;
        const bRole = b.user.role;
        
        // Coach role (2) should come first
        if (aRole === 2 && bRole !== 2) return -1;
        if (bRole === 2 && aRole !== 2) return 1;
        
        // If both are coaches or both are not coaches, maintain original order
        return 0;
      });
      
      console.log('Fetched participants:', sortedParticipants);
      // Force re-render by creating a new array
      setParticipants([...sortedParticipants]);
      
      // Fetch forfait status for riders
      await fetchForfaitStatuses(sortedParticipants);
      await fetchSubscriptionStatuses(sortedParticipants);
      await fetchJetonStatuses(sortedParticipants);
    } catch (error) {
      console.error('Error fetching participants:', error);
      toast.error('Failed to fetch participants');
    }
  };

  const fetchForfaitStatuses = async (participants) => {
    const riderParticipants = participants.filter(p => p.user.role === 0); // Riders only
    
    for (const participant of riderParticipants) {
      try {
        let url = createApiUrlWithPrefix(`sessions/rider/${participant.user.id}/forfait-status`);
        if (selectedDate) {
          url += `?targetDate=${selectedDate}`;
        }
        
        const response = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setForfaitStatuses(prev => ({
          ...prev,
          [participant.user.id]: response.data
        }));
      } catch (error) {
        console.error(`Error fetching forfait status for rider ${participant.user.id}:`, error);
        // Set default status if fetch fails
        setForfaitStatuses(prev => ({
          ...prev,
          [participant.user.id]: {
            hasForfait: false,
            message: t('attendance.unableToLoadForfaitStatus'),
            remainingLessons: 0
          }
        }));
      }
    }
  };

  const fetchSubscriptionStatuses = async (participants) => {
    const ownerParticipants = participants.filter(p => p.user.role === 1); // Owners only
    
    for (const participant of ownerParticipants) {
      try {
        const response = await axios.get(createApiUrlWithPrefix(`sessions/owner/${participant.user.id}/subscription-status`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        setSubscriptionStatuses(prev => ({
          ...prev,
          [participant.user.id]: response.data
        }));
      } catch (error) {
        console.error(`Error fetching subscription status for owner ${participant.user.id}:`, error);
        // Set default status if fetch fails
        setSubscriptionStatuses(prev => ({
          ...prev,
          [participant.user.id]: {
            hasSubscription: false,
            message: t('attendance.unableToLoadSubscriptionStatus'),
            tier: 0
          }
        }));
      }
    }
  };

  const fetchJetonStatuses = async (participants) => {
    const temporaryParticipants = participants.filter(p => p.user.role === 5); // Temporary users only
    
    for (const participant of temporaryParticipants) {
      try {
             const response = await axios.get(createApiUrlWithPrefix(`Jeton/user/${participant.user.id}`), {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });
        
        // Calculate total remaining jetons
        const jetons = response.data || [];
        const totalRemaining = jetons.reduce((sum, jeton) => sum + jeton.remainingQuantity, 0);
        const totalUsed = jetons.reduce((sum, jeton) => sum + jeton.usedQuantity, 0);
        
        setJetonStatuses(prev => ({
          ...prev,
          [participant.user.id]: {
            hasJetons: totalRemaining > 0,
            remainingJetons: totalRemaining,
            usedJetons: totalUsed,
            totalJetons: totalRemaining + totalUsed,
            message: totalRemaining > 0 ? t('attendance.jetonsRemaining', { count: totalRemaining }) : t('attendance.noJetonsAvailable')
          }
        }));
      } catch (error) {
        console.error(`Error fetching jeton status for temporary user ${participant.user.id}:`, error);
        // Set default status if fetch fails - show that jeton system is not available
        setJetonStatuses(prev => ({
          ...prev,
          [participant.user.id]: {
            hasJetons: false,
            remainingJetons: 0,
            usedJetons: 0,
            totalJetons: 0,
            message: t('attendance.jetonSystemNotAvailable')
          }
        }));
      }
    }
  };

  const markAttendance = async (sessionId, userId, status) => {
    try {
      // Get current user ID with validation
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        toast.error('User session expired. Please login again.');
        return;
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(createApiUrlWithPrefix(`sessions/${sessionId}/attendance?attendanceDate=${selectedDate}`), {
        userId: userId,
        status: status,
        markedByUserId: parseInt(currentUserId) // Send the actual logged-in user ID
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      // Show success message with appropriate info based on user type
      const participant = participants.find(p => p.user.id === userId);
      if (participant && status === 1) { // User marked as Present
        if (participant.user.role === 0) { // Rider
          const forfaitStatus = forfaitStatuses[userId];
          const bonusLessons = forfaitStatus?.bonusLessons ?? forfaitStatus?.BonusLessons ?? 0;
          if (forfaitStatus && forfaitStatus.hasForfait) {
            toast.success(`Attendance marked! Forfait: ${forfaitStatus.remainingLessons - 1} lessons remaining`);
          } else if (bonusLessons > 0) {
            toast.success('Attendance marked! Bonus lesson used');
          } else {
            toast.success('Attendance marked! (No active forfait)');
          }
        } else if (participant.user.role === 5) { // Temporary User
          toast.success('Attendance marked! 1 jeton deducted from balance');
        } else {
          toast.success('Attendance marked successfully');
        }
      } else {
        toast.success(t('sessions.simplified.messages.participantAssigned'));
      }
      
      // Refresh participants and forfait status
      console.log('Refreshing participants after attendance marking...');
      toast.loading('Updating attendance status...', { id: 'refresh-attendance' });
      // Add a small delay to ensure backend has processed the attendance
      await new Promise(resolve => setTimeout(resolve, 500));
      await fetchSessionParticipants(sessionId);
      toast.dismiss('refresh-attendance');
      console.log('Participants refreshed');
    } catch (error) {
      console.error('Error marking attendance:', error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error(t('sessions.simplified.messages.failedToAssign'));
      }
    }
  };

  const openParticipantsModal = async (session) => {
    setSelectedSession(session);
    setShowParticipantsModal(true);
    await fetchSessionParticipants(session.id);
  };

  const openHorseAttendanceModal = async (session) => {
    setSelectedSession(session);
    setShowHorseAttendanceModal(true);
    await fetchSessionHorses(session.id);
  };

  const fetchSessionHorses = async (sessionId) => {
    try {
      console.log('Fetching horses for session:', sessionId);
      console.log('Selected date for horse attendance:', selectedDate);
      const token = localStorage.getItem('token');
      const response = await axios.get(createApiUrlWithPrefix(`sessions/${sessionId}/horses`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      console.log('Horses response:', response.data);
      setSessionHorses(response.data || []);
      
      // Fetch horse attendances for the specific date
      const attendanceResponse = await axios.get(createApiUrlWithPrefix(`sessions/${sessionId}/horse-attendances`), {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        params: {
          attendanceDate: selectedDate // Pass the selected date for daily attendance
        }
      });
      console.log('Horse attendances response for date', selectedDate, ':', attendanceResponse.data);
      
      // Convert array to object for easy lookup
      const attendances = {};
      (attendanceResponse.data || []).forEach(attendance => {
        attendances[attendance.horseId] = attendance;
        console.log(`Horse ${attendance.horseId} attendance for ${selectedDate}:`, attendance);
      });
      setHorseAttendances(attendances);
      console.log('Final horse attendances object:', attendances);
      console.log('Number of horse attendances found:', Object.keys(attendances).length);
    } catch (error) {
      console.error('Failed to fetch session horses:', error);
      setSessionHorses([]);
      setHorseAttendances({});
    }
  };

  const markHorseAttendance = async (sessionId, horseId, status, notes = '') => {
    try {
      console.log('Marking horse attendance:', { sessionId, horseId, status, notes, selectedDate });
      const token = localStorage.getItem('token');
      const userId = localStorage.getItem('userId');
      
      const response = await axios.post(createApiUrlWithPrefix(`sessions/${sessionId}/mark-horse-attendance?attendanceDate=${selectedDate}`), {
        horseId,
        status,
        markedByUserId: userId ? parseInt(userId) : null,
        notes
      }, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      console.log('Horse attendance marked successfully:', response.data);
      
      // Update local state
      setHorseAttendances(prev => ({
        ...prev,
        [horseId]: {
          ...prev[horseId],
          status,
          notes,
          markedAt: new Date().toISOString(),
          attendanceDate: selectedDate
        }
      }));
      
      console.log('Updated local horse attendances state');
      toast.success(t('attendance.horseAttendance.attendanceMarkedSuccessfully'));
    } catch (error) {
      console.error('Failed to mark horse attendance:', error);
      toast.error(t('attendance.horseAttendance.failedToMarkAttendance'));
    }
  };

  const getAttendanceStatusIcon = (status) => {
    switch (status) {
      case 1: return <CheckCircle className="w-4 h-4 text-green-500" />;
      case 2: return <XCircle className="w-4 h-4 text-red-500" />;
      case 3: return <Clock className="w-4 h-4 text-yellow-500" />;
      default: return <Clock className="w-4 h-4 text-gray-400" />;
    }
  };

  const getAttendanceStatusText = (status) => {
    switch (status) {
      case 1: return t('sessions.pending');
      case 2: return t('sessions.absent');
      case 3: return t('sessions.excused');
      default: return t('sessions.pending');
    }
  };

  const getAttendanceStatusColor = (status) => {
    switch (status) {
      case 1: return 'text-green-600 bg-green-100';
      case 2: return 'text-red-600 bg-red-100';
      case 3: return 'text-yellow-600 bg-yellow-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getRoleText = (role) => {
    const roleValue = typeof role === 'string' ? role.toLowerCase() : role;
    
    switch (roleValue) {
      case 0:
      case 'rider': return t('sessions.roles.rider');
      case 1:
      case 'owner': return t('sessions.roles.owner');
      case 2:
      case 'coach': return t('sessions.roles.coach');
      case 3:
      case 'admin': return t('sessions.roles.admin');
      case 4:
      case 'superadmin': return t('sessions.roles.superAdmin');
      case 5:
      case 'temporary': return 'Temporary User';
      default: return t('sessions.roles.unknown');
    }
  };

  const shouldShowWalletBalance = (role) => {
    const roleValue = typeof role === 'string' ? role.toLowerCase() : role;
    // Only riders have a point system, not owners
    return roleValue === 0 || roleValue === 'rider';
  };

  const shouldShowJetonInfo = (role) => {
    const roleValue = typeof role === 'string' ? role.toLowerCase() : role;
    // Only temporary users have jeton system
    return roleValue === 5 || roleValue === 'temporary';
  };

  const getRoleColor = (role) => {
    const roleValue = typeof role === 'string' ? role.toLowerCase() : role;
    
    switch (roleValue) {
      case 0:
      case 'rider': return 'bg-green-100 text-green-800';
      case 1:
      case 'owner': return 'bg-orange-100 text-orange-800';
      case 2:
      case 'coach': return 'bg-blue-100 text-blue-800';
      case 3:
      case 'admin': return 'bg-purple-100 text-purple-800';
      case 4:
      case 'superadmin': return 'bg-red-100 text-red-800';
      case 5:
      case 'temporary': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatTime = (timeString) => {
    return timeString.substring(0, 5);
  };

  const getLevelColor = (level) => {
    switch (level) {
      case 'Beginner': return 'bg-green-100 text-green-800';
      case 'Intermediate': return 'bg-yellow-100 text-yellow-800';
      case 'Professional': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'Weekly': return 'bg-blue-100 text-blue-800';
      case 'Daily': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getTargetColor = (target) => {
    switch (target) {
      case 'Rider': return 'bg-green-100 text-green-800';
      case 'Owner': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getHorseAttendanceStatusIcon = (status) => {
    switch (status) {
      case 1: return <CheckCircle className="w-4 h-4 text-green-500" />; // Worked
      case 2: return <Clock className="w-4 h-4 text-blue-500" />; // Rest
      case 3: return <XCircle className="w-4 h-4 text-red-500" />; // Injured
      default: return <Clock className="w-4 h-4 text-gray-400" />; // Not Worked
    }
  };

  const getHorseAttendanceStatusText = (status) => {
    switch (status) {
      case 1: return t('attendance.horseAttendance.statuses.worked');
      case 2: return t('attendance.horseAttendance.statuses.rest');
      case 3: return t('attendance.horseAttendance.statuses.injured');
      default: return t('attendance.horseAttendance.statuses.notWorked');
    }
  };

  const getHorseAttendanceStatusColor = (status) => {
    switch (status) {
      case 1: return 'text-green-600 bg-green-100';
      case 2: return 'text-blue-600 bg-blue-100';
      case 3: return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  // Filter sessions for the selected date
  const getSessionsForDate = (date) => {
    if (!date) return [];
    
    const dateStr = date.toISOString().split('T')[0];
    const dayOfWeek = date.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const currentDayName = dayNames[dayOfWeek];
    
    // Get daily sessions (exact date match)
    const dailySessions = sessions.filter(session => {
      const sessionDate = new Date(session.date).toISOString().split('T')[0];
      return sessionDate === dateStr;
    });
    
    // Get weekly sessions (check if current day matches weekly days)
    const weeklySessions = sessions.filter(session => {
      return (session.type === 0 || session.type === 'Weekly') && // Weekly session
             session.weeklyDays && 
             session.weeklyDays.includes(currentDayName);
    });
    
    // Combine both types and remove duplicates based on session ID
    const allSessions = [...dailySessions, ...weeklySessions];
    const uniqueSessions = allSessions.filter((session, index, self) => 
      index === self.findIndex(s => s.id === session.id)
    );
    
    return uniqueSessions;
  };

  // Get sessions for the selected date
  const todaysSessions = useMemo(() => {
    return getSessionsForDate(new Date(selectedDate));
  }, [selectedDate, sessions]);

  // Calendar navigation functions
  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const navigateWeek = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + (direction * 7));
    setCurrentDate(newDate);
  };

  const navigateDay = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setDate(currentDate.getDate() + direction);
    setCurrentDate(newDate);
  };

  const goToToday = () => {
    setCurrentDate(new Date());
    setSelectedDate(new Date().toISOString().split('T')[0]);
  };

  /* removed early loading return to prevent search bar unmounting 
if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  } 
*/

  return (
    <div className="space-y-6 p-4 sm:p-6 lg:p-8" dir={isRTL ? 'rtl' : 'ltr'} key={refreshKey}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{t('attendance.title')}</h1>
          <p className="mt-2 text-gray-600">{t('attendance.subtitle')}</p>
        </div>
        
        <div className="flex items-center space-x-3">
          {/* Refresh Button */}
          <button
            onClick={handleRefresh}
            className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            title="Refresh data"
          >
            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
          
          {/* View Mode Toggle */}
          <div className="flex bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setViewMode('daily')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'daily' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <List className="w-4 h-4 inline mr-1" />
              {t('attendance.dailyView')}
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                viewMode === 'calendar' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-600'
              }`}
            >
              <CalendarIcon className="w-4 h-4 inline mr-1" />
              {t('attendance.calendarView')}
            </button>
          </div>
        </div>
      </div>

      {/* Search and Date Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-4">
          {/* Date Picker */}
          <div className="flex items-center space-x-2">
            <Calendar className="w-4 h-4 text-gray-400" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          
          {/* Search */}
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder={t('attendance.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent w-full"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      {viewMode === 'calendar' ? (
        <CalendarView
          currentDate={currentDate}
          sessions={sessions}
          onNavigateMonth={navigateMonth}
          onNavigateWeek={navigateWeek}
          onNavigateDay={navigateDay}
          onGoToToday={goToToday}
          onEditSession={openParticipantsModal}
          onManageParticipants={openParticipantsModal}
          onDeleteSession={() => {}}
          formatTime={formatTime}
          getLevelColor={getLevelColor}
          getTypeColor={getTypeColor}
          getTargetColor={getTargetColor}
        />
      ) : (
        <div className="space-y-6 p-4 sm:p-6 lg:p-8">
          {/* Today's Sessions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200">
            <div className="px-6 py-4 border-b border-gray-200">
              <h2 className="text-lg font-semibold text-gray-900">
                {t('attendance.todaysSessions')} - {new Date(selectedDate).toLocaleDateString()}
              </h2>
              <p className="text-sm text-gray-600">
                {todaysSessions.length} {t('attendance.sessionsFound')}
              </p>
            </div>
            
            <div className="divide-y divide-gray-200">
              {todaysSessions.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>{t('attendance.noSessionsToday')}</p>
                </div>
              ) : (
                todaysSessions.map((session) => (
                  <div key={session.id} className="p-6 hover:bg-gray-50 transition-colors">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center space-x-3 mb-3">
                          <h4 className="text-lg font-semibold text-gray-900">
                            {session.name || session.title || `${session.levelName} Level Session`}
                          </h4>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getLevelColor(session.level)}`}>
                            {session.levelName || session.level}
                          </span>
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getTypeColor(session.type)}`}>
                            {session.typeName || session.type}
                          </span>
                          {(session.type === 'Weekly' || session.type === 0) && session.weeklyDays && (
                            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold bg-purple-200 text-purple-900 border border-purple-300">
                              📅 {session.weeklyDays.split(',').map(day => day.trim()).join(', ')}
                            </span>
                          )}
                        </div>
                        
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                          <div className="flex items-center text-sm text-gray-600">
                            <Calendar className="w-4 h-4 mr-2" />
                            <span>{new Date(session.date).toLocaleDateString()}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Clock className="w-4 h-4 mr-2" />
                            <span>{formatTime(session.startTime)} - {formatTime(session.endTime)}</span>
                          </div>
                          <div className="flex items-center text-sm text-gray-600">
                            <Users className="w-4 h-4 mr-2" />
                            <span>{session.participants?.length || 0} {t('sessions.participants')}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => openParticipantsModal(session)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-green-600 bg-green-50 rounded-md hover:bg-green-100 transition-colors"
                        >
                          <UserPlus className="w-4 h-4 mr-1" />
                          {t('attendance.manageAttendance')}
                        </button>
                        <button
                          onClick={() => openHorseAttendanceModal(session)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100 transition-colors"
                        >
                          <span className="text-lg mr-1">🐎</span>
                          {t('attendance.horseAttendance.title')}
                        </button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}

      {/* Participants Modal */}
      {showParticipantsModal && selectedSession && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">
                  {t('attendance.manageAttendance')} - {selectedSession.name || selectedSession.title}
                </h3>
                <button
                  onClick={() => setShowParticipantsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="w-6 h-6" />
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[70vh]">
              {participants.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>{t('attendance.noParticipants')}</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('attendance.participant')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('attendance.role')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('attendance.status')}
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          {t('attendance.actions')}
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {participants.map((participant) => (
                        <tr key={participant.id} className={participant.user?.role === 2 ? 'bg-blue-50' : ''}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className={`h-10 w-10 rounded-full flex items-center justify-center ${participant.user?.role === 2 ? 'bg-blue-300' : 'bg-gray-300'}`}>
                                  <User className={`h-6 w-6 ${participant.user?.role === 2 ? 'text-blue-600' : 'text-gray-600'}`} />
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900 flex items-center">
                                  {participant.user?.firstName} {participant.user?.lastName}
                                  {participant.user?.role === 2 && (
                                    <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                      👨‍🏫 Coach
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {participant.user?.email}
                                </div>
                                {participant.user?.role === 0 && forfaitStatuses[participant.user.id] && (
                                  <div className="text-xs text-blue-600 mt-1">
                                    {(() => {
                                      const fs = forfaitStatuses[participant.user.id];
                                      const bonusLessons = fs.bonusLessons ?? fs.BonusLessons ?? 0;
                                      if (fs.hasForfait) {
                                        return (
                                          <span>
                                            📚 {fs.remainingLessons} lessons remaining
                                            {fs.extraLessons > 0 && (
                                              <span className="ml-2 text-green-600">
                                                +{fs.extraLessons} extra
                                              </span>
                                            )}
                                            {bonusLessons > 0 && (
                                              <span className="ml-2 text-purple-600">
                                                🎁 +{bonusLessons} bonus
                                              </span>
                                            )}
                                          </span>
                                        );
                                      }
                                      if (bonusLessons > 0) {
                                        return (
                                          <span className="text-purple-600">
                                            🎁 {bonusLessons} bonus lessons available
                                          </span>
                                        );
                                      }
                                      return <span className="text-red-600">⚠️ No active forfait</span>;
                                    })()}
                                  </div>
                                )}
                                {participant.user?.role === 1 && subscriptionStatuses[participant.user.id] && (
                                  <div className="text-xs text-purple-600 mt-1">
                                    {subscriptionStatuses[participant.user.id].hasSubscription ? (
                                      <span>
                                        🏆 {subscriptionStatuses[participant.user.id].tierName} Subscription
                                        {subscriptionStatuses[participant.user.id].maxHorses > 0 && (
                                          <span className="ml-2 text-gray-600">
                                            ({subscriptionStatuses[participant.user.id].maxHorses} horses)
                                          </span>
                                        )}
                                      </span>
                                    ) : (
                                      <span className="text-red-600">⚠️ No active subscription</span>
                                    )}
                                  </div>
                                )}
                                {participant.user?.role === 5 && (
                                  <div className="text-xs text-orange-600 mt-1">
                                    {jetonStatuses[participant.user.id] ? (
                                      jetonStatuses[participant.user.id].hasJetons ? (
                                        <span>
                                          🎫 {jetonStatuses[participant.user.id].remainingJetons} jetons remaining
                                          {jetonStatuses[participant.user.id].usedJetons > 0 && (
                                            <span className="ml-2 text-gray-600">
                                              ({jetonStatuses[participant.user.id].usedJetons} used)
                                            </span>
                                          )}
                                        </span>
                                      ) : (
                                        <span className="text-red-600">
                                          🎫 No jetons available
                                        </span>
                                      )
                                    ) : (
                                      <span className="text-gray-600">
                                        🎫 Loading jeton status...
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getRoleColor(participant.user?.role)}`}>
                              {getRoleText(participant.user?.role)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getAttendanceStatusIcon(participant.attendanceStatus)}
                              <span className={`ml-2 px-2 py-1 rounded-full text-xs font-medium ${getAttendanceStatusColor(participant.attendanceStatus)}`}>
                                {getAttendanceStatusText(participant.attendanceStatus)}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex items-center justify-center">
                              {participant.attendanceStatus === 1 ? (
                                <div className="flex items-center text-green-600">
                                  <CheckCircle className="w-6 h-6 mr-2" />
                                  <span className="font-medium">{t('sessions.present')}</span>
                                </div>
                              ) : participant.attendanceStatus === 2 ? (
                                <div className="flex items-center text-red-600">
                                  <XCircle className="w-6 h-6 mr-2" />
                                  <span className="font-medium">{t('sessions.absent')}</span>
                                </div>
                              ) : participant.attendanceStatus === 3 ? (
                                <div className="flex items-center text-yellow-600">
                                  <AlertCircle className="w-6 h-6 mr-2" />
                                  <span className="font-medium">{t('sessions.excused')}</span>
                                </div>
                              ) : (
                                <div className="flex items-center space-x-1">
                                  {/* Present Button */}
                                  <button
                                    onClick={() => {
                                      // Check forfait status for riders
                                      if (participant.user.role === 0) {
                                        const forfaitStatus = forfaitStatuses[participant.user.id];
                                        const bonusLessons = forfaitStatus?.bonusLessons ?? forfaitStatus?.BonusLessons ?? 0;
                                        const totalAvailable = (forfaitStatus?.remainingLessons ?? 0) + (forfaitStatus?.extraLessons ?? 0) + bonusLessons;
                                        if (forfaitStatus && !forfaitStatus.hasForfait && bonusLessons <= 0) {
                                          toast.error('Cannot mark present: No active forfait');
                                          return;
                                        }
                                        // Check if rider has any lessons available (monthly + extra + bonus)
                                        if (forfaitStatus && totalAvailable <= 0) {
                                          toast.error('Cannot mark present: No lessons remaining');
                                          return;
                                        }
                                      }
                                      // Check subscription status for owners
                                      if (participant.user.role === 1) {
                                        const subscriptionStatus = subscriptionStatuses[participant.user.id];
                                        if (subscriptionStatus && !subscriptionStatus.hasSubscription) {
                                          toast.error('Cannot mark present: No active subscription');
                                          return;
                                        }
                                      }
                                      // Check jeton status for temporary users
                                      if (participant.user.role === 5) {
                                        const jetonStatus = jetonStatuses[participant.user.id];
                                        if (jetonStatus && !jetonStatus.hasJetons) {
                                          toast.error('Cannot mark present: No active jetons available');
                                          return;
                                        }
                                        if (jetonStatus && jetonStatus.remainingJetons <= 0) {
                                          toast.error('Cannot mark present: No jetons remaining');
                                          return;
                                        }
                                      }
                                      markAttendance(selectedSession.id, participant.user.id, 1);
                                    }}
                                    className="text-gray-400 hover:text-green-600 flex items-center transition-colors duration-200 p-2 rounded-full hover:bg-green-50"
                                    title={t('sessions.markPresent')}
                                  >
                                    <CheckCircle className="w-5 h-5" />
                                  </button>
                                  
                                  {/* Absent Button */}
                                  <button
                                    onClick={() => markAttendance(selectedSession.id, participant.user.id, 2)}
                                    className="text-gray-400 hover:text-red-600 flex items-center transition-colors duration-200 p-2 rounded-full hover:bg-red-50"
                                    title={t('attendance.markAbsent', 'Mark as Absent')}
                                  >
                                    <XCircle className="w-5 h-5" />
                                  </button>
                                  
                                  {/* Excused Button */}
                                  <button
                                    onClick={() => markAttendance(selectedSession.id, participant.user.id, 3)}
                                    className="text-gray-400 hover:text-yellow-600 flex items-center transition-colors duration-200 p-2 rounded-full hover:bg-yellow-50"
                                    title={t('attendance.markExcused', 'Mark as Excused')}
                                  >
                                    <AlertCircle className="w-5 h-5" />
                                  </button>
                                </div>
                              )}
                            </div>
                            {shouldShowWalletBalance(participant.user?.role) && participant.attendanceStatus === 1 && (
                              <div className="text-xs text-blue-600 mt-1 flex items-center">
                                <Wallet className="w-3 h-3 mr-1" />
                                1 point deducted
                              </div>
                            )}
                            {shouldShowJetonInfo(participant.user?.role) && participant.attendanceStatus === 1 && (
                              <div className="text-xs text-orange-600 mt-1 flex items-center">
                                <Wallet className="w-3 h-3 mr-1" />
                                1 jeton deducted
                              </div>
                            )}
                            {(participant.user?.role === 1 || participant.user?.role === 2) && participant.attendanceStatus === 1 && (
                              <div className="text-xs text-green-600 mt-1 flex items-center">
                                <CheckCircle className="w-3 h-3 mr-1" />
                                No points deducted
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Horse Attendance Modal */}
      {showHorseAttendanceModal && selectedSession && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-4/5 max-w-6xl shadow-lg rounded-md bg-white">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900 flex items-center">
                  <span className="text-2xl mr-2">🐎</span>
                  {t('attendance.horseAttendance.horseAttendanceFor', { sessionTitle: selectedSession.name || selectedSession.title })}
                </h3>
                <button
                  onClick={() => setShowHorseAttendanceModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <span className="sr-only">Close</span>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              
              <div className="mb-4 text-sm text-gray-600">
                <p><strong>Session Date:</strong> {new Date(selectedSession.date).toLocaleDateString()}</p>
                <p><strong>Attendance Date:</strong> {new Date(selectedDate).toLocaleDateString()}</p>
                <p><strong>Time:</strong> {formatTime(selectedSession.startTime)} - {formatTime(selectedSession.endTime)}</p>
                <p><strong>Description:</strong> {selectedSession.description || 'No description'}</p>
                <p className="mt-2 text-sm text-gray-500">
                  {sessionHorses.length} horses assigned to this session
                </p>
                <div className="mt-2 p-2 bg-blue-50 rounded-lg">
                  <p className="text-blue-800 text-xs">
                    <strong>Note:</strong> Horse attendance is tracked per day. Each day has its own attendance records.
                  </p>
                </div>
              </div>
              
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('attendance.horseAttendance.horseName')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('attendance.horseAttendance.owner')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('attendance.horseAttendance.rider')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('attendance.horseAttendance.attendanceStatus')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        {t('attendance.actions')}
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {sessionHorses.length === 0 ? (
                      <tr>
                        <td colSpan="5" className="px-6 py-4 text-center text-gray-500">
                          <div className="flex flex-col items-center">
                            <span className="text-4xl mb-2">🐎</span>
                            <p className="text-lg font-medium">{t('attendance.horseAttendance.noHorsesAssigned')}</p>
                            <p className="text-sm">Go to "Gestion Simplifiée des Séances" to assign horses to this session</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      sessionHorses.map((horse) => {
                      const attendance = horseAttendances[horse.horseId];
                      const currentStatus = attendance?.status || 0;
                      
                      return (
                        <tr key={horse.id}>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="flex-shrink-0 h-10 w-10">
                                <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                                  <span className="text-lg">🐎</span>
                                </div>
                              </div>
                              <div className="ml-4">
                                <div className="text-sm font-medium text-gray-900">
                                  {horse.horseName}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {horse.horseBreed || 'Unknown breed'}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {horse.ownerName}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                            {horse.riderName || 'No rider assigned'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              {getHorseAttendanceStatusIcon(currentStatus)}
                              <span className={`ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getHorseAttendanceStatusColor(currentStatus)}`}>
                                {getHorseAttendanceStatusText(currentStatus)}
                              </span>
                            </div>
                            {attendance?.notes && (
                              <div className="text-xs text-gray-500 mt-1">
                                Note: {attendance.notes}
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                            <div className="flex space-x-2">
                              <button
                                onClick={() => markHorseAttendance(selectedSession.id, horse.horseId, 1)}
                                className={`text-green-600 hover:text-green-900 flex items-center ${
                                  currentStatus === 1 ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={currentStatus === 1}
                              >
                                <CheckCircle className="w-4 h-4 mr-1" />
                                {t('attendance.horseAttendance.actions.markWorked')}
                              </button>
                              <button
                                onClick={() => markHorseAttendance(selectedSession.id, horse.horseId, 2)}
                                className={`text-blue-600 hover:text-blue-900 flex items-center ${
                                  currentStatus === 2 ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={currentStatus === 2}
                              >
                                <Clock className="w-4 h-4 mr-1" />
                                {t('attendance.horseAttendance.actions.markRest')}
                              </button>
                              <button
                                onClick={() => markHorseAttendance(selectedSession.id, horse.horseId, 3)}
                                className={`text-red-600 hover:text-red-900 flex items-center ${
                                  currentStatus === 3 ? 'opacity-50 cursor-not-allowed' : ''
                                }`}
                                disabled={currentStatus === 3}
                              >
                                <XCircle className="w-4 h-4 mr-1" />
                                {t('attendance.horseAttendance.actions.markInjured')}
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnhancedAttendanceManagement;
